import { useMemo } from 'react';
import useStore from '@components/MainStage/store';
import { DomainItem } from '@constants/forms/scope-settings';
import useProjectId from '@hooks/useProjectId';
import { skipToken } from '@reduxjs/toolkit/query';
import { DomainNode } from '@store/services/nodes/types';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { parseDomains } from '@utils/helpers';

const useVirtualDomains = () => {
  const projectId = useProjectId();
  const { getActiveNode } = useStore();
  const activeNode = getActiveNode();

  const {
    data: projectDomains,
    isLoading,
    isFetching,
  } = useGetProjectDomainsQuery(projectId || skipToken);

  const result = useMemo(() => {
    if (!projectDomains) {
      return {
        inheritedDomains: [] as DomainItem[],
      };
    }

    const domainNodes = (activeNode?.data?.dto?.domainNodes ||
      []) as DomainNode[];

    // Check if there are any inherited domain nodes
    // If no Technology is associated with the entity (any layer above Technology),
    // or the associated Technology has no Operationalised Scope defined,
    // return empty array to display "No underpinning Operational Control"
    const hasInheritedDomains = domainNodes.some(
      ({ type }) => type === 'inherited',
    );

    if (!hasInheritedDomains) {
      return {
        inheritedDomains: [] as DomainItem[],
      };
    }

    return {
      inheritedDomains: parseDomains(
        projectDomains,
        (id) =>
          !domainNodes.some(
            ({ domain_id, type }) => domain_id === id && type === 'inherited',
          ),
      ),
    };
  }, [activeNode?.data?.dto?.id, projectDomains]);

  return {
    ...result,
    isLoading: isLoading || isFetching,
  };
};

export default useVirtualDomains;
